import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { BadgeCheck, SearchX } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/brand/Logo";
import { api, formatApiError } from "@/lib/api";
import { formatDate } from "@/lib/constants";

export default function Verify() {
  const { code } = useParams();
  const navigate = useNavigate();
  const [value, setValue] = useState(code || "");
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!code) return;
    setResult(null);
    setError(null);
    api.get(`/public/certificates/${encodeURIComponent(code)}`).then((r) => setResult(r.data)).catch((e) => setError(formatApiError(e)));
  }, [code]);

  return (
    <div className="min-h-screen bg-lasso-paper">
      <header className="mx-auto max-w-3xl px-6 py-6"><Link to="/"><Logo size="sm" /></Link></header>
      <main className="mx-auto max-w-xl px-6 pb-24 pt-10">
        <h1 className="font-display text-3xl font-bold tracking-tight">Verify a certificate</h1>
        <p className="mt-2 text-muted-foreground">Enter the certificate ID printed on a Lasso Hub certificate.</p>
        <form className="mt-8 flex gap-2" onSubmit={(e) => { e.preventDefault(); if (value.trim()) navigate(`/verify/${value.trim().toUpperCase()}`); }}>
          <Input value={value} onChange={(e) => setValue(e.target.value)} placeholder="LH-2026-XXXXXX" className="h-12 bg-white font-mono" data-testid="verify-code-input" />
          <button className="rounded-full bg-lasso-ink px-6 text-sm font-semibold text-white hover:bg-lasso-navy" data-testid="verify-submit-button">Verify</button>
        </form>
        {result && (
          <div className={`mt-8 rounded-3xl border bg-white p-7 ${result.status === "expired" || result.status === "superseded" ? "border-rose-200" : "border-emerald-200"}`} data-testid={result.status === "expired" || result.status === "superseded" ? "verify-result-expired" : "verify-result-valid"}>
            <BadgeCheck className={`h-7 w-7 ${result.status === "expired" || result.status === "superseded" ? "text-rose-500" : "text-emerald-600"}`} />
            <p className={`mt-4 text-xs font-semibold uppercase tracking-[0.2em] ${result.status === "expired" || result.status === "superseded" ? "text-rose-600" : "text-emerald-700"}`}>{result.status === "expired" || result.status === "superseded" ? "Expired certificate" : "Valid certificate"}</p>
            <p className="mt-2 font-display text-xl font-bold">{result.user_name}</p>
            <p className="mt-1 text-zinc-600">completed <strong>{result.course_title}</strong> on {formatDate(result.issued_at)}</p>
            {result.expires_at && <p className="mt-1 text-sm text-zinc-600" data-testid="verify-expiry">{result.status === "expired" || result.status === "superseded" ? "Expired" : "Valid until"} {formatDate(result.expires_at)}</p>}
            <p className="mt-3 font-mono text-xs text-zinc-500">{result.code}</p>
          </div>
        )}
        {error && (
          <div className="mt-8 flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-5 text-sm text-rose-800" data-testid="verify-result-invalid">
            <SearchX className="h-5 w-5" /> {error}
          </div>
        )}
      </main>
    </div>
  );
}
